import { Inject, Injectable, Optional, SkipSelf } from "@angular/core";

import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ActivityAwareDataSubService } from '../../../shared/basic-data-management/activity-aware-data-subservice.class';
import { CachedDataSurveyService } from '../../../technical-modules/tasks/services/cached-data-survey.service';

import { EntrepriseTLR } from '../entities/entreprise-tlr.entity';
import { EntrepriseDTO } from '../entities/entreprise-dto.entity';
import { EntreprisesFilters } from '../entities/entreprises-filters.entity';
import { Suggestions } from "../../../shared/suggestable-input/entities/suggestions.entity";
import { ActivePossibilities } from "../../../shared/suggestable-input/entities/active-possibilities.entity";
import { SuggestionNomEntreprise } from "../entities/suggestion-nom-entreprise.entity";

@Injectable({ providedIn: 'root' })
export class EntrepriseService {
	private path: string;

	private entrepriseTLRSubService!: ActivityAwareDataSubService<EntrepriseTLR>;
	
	private notifyChanges = () => {
		this.entrepriseTLRSubService.clearCaches();
		// Indique immediatement un changement sur les donnees aux caches sans que le systeme de surveillance ait a attendre sa prochaine verification 
		this.cachedDataSurveyService.notifyImmediateChange("Entreprise");
	};

	constructor( //
		@Inject(HttpClient) private httpClient: HttpClient, //
		@Inject(CachedDataSurveyService) private cachedDataSurveyService: CachedDataSurveyService, //
		@Optional() @SkipSelf() previousInstance?: EntrepriseService //
	) {
		if (previousInstance) {
			throw new Error(
				'This service is already loaded. Import it in the AppModule only');
		}
		this.path = (window as any).baseApiPath;
		this.entrepriseTLRSubService = new ActivityAwareDataSubService<EntrepriseTLR>(httpClient, this.path + 'entreprises?format=TLR', (entreprise: EntrepriseTLR) => !entreprise.obsolete);

		let entreprisesChanged$ = this.cachedDataSurveyService.getChangeNotification("Entreprise");
	}

	// Methodes permettant le chargement de toutes les entreprises
	public getEntreprisesTLRMapById = () => this.entrepriseTLRSubService.getMapById();
	public getEntrepriseTLRById = (id: number) => this.entrepriseTLRSubService.getKnownById(id, "EntrepriseTLR", );


	public getAllEntreprises = () => this.entrepriseTLRSubService.getList();
	
	// Methodes permettant le chargement des entreprises actives
	public getActiveEntreprisesTLRList = () => this.entrepriseTLRSubService.getActiveList();
	public getActiveEntreprisesTLRMapById = () => this.entrepriseTLRSubService.getMapActiveById();
	public getActiveEntrepriseTLRById = (id: number) => this.entrepriseTLRSubService.getKnownActiveById(id, "EntrepriseTLR", );
	
	public getEntreprisesByOperation(operationId: number): Observable<EntrepriseDTO[]> {
		let url = this.path + "operations/" + operationId + '/entreprises';
		let result = this.httpClient //
			.get<EntrepriseDTO[]>(url) //
			;

		return result;
	}

	public getEntreprisesByAcces(accesId: number): Observable<EntrepriseTLR[]> {
		let url = this.path + "acces/" + accesId + '/entreprises';
		let result = this.httpClient //
			.get<EntrepriseTLR[]>(url) //
			;

		return result;
	}
	
	// Fonctions CRUD

	public create(base: EntrepriseTLR): Observable<number> {
		let url = this.path + 'entreprises';
		let result = this.httpClient.post<number>(url, base).pipe(
			tap(this.notifyChanges)
		);
		return result;
	}

	public update(base: EntrepriseTLR): Observable<number> {
		let url = this.path + 'entreprises/' + base.id;
		let result = this.httpClient.put<number>(url, base).pipe(
			tap(this.notifyChanges)
		);
		return result;
	}

	public delete(entrepriseId: number): Observable<any> {
		const url = this.path + 'entreprises/' + entrepriseId;
		const request$ = this.httpClient.delete<any>(url, {}).pipe(
			tap(this.notifyChanges)
		);
		return request$;
	}

	// Fonction d'export en XLS
	public export(filters: EntreprisesFilters): Observable<Blob> {
		let url = this.path + 'entreprises/export';
		
		let httpParams = new HttpParams();
		for (let key in filters) {
			let value = (filters as any)[key];
			if (value !== null && value !== undefined) {
				if (key === 'actif') {
					httpParams = httpParams.set(key, value ? '1' : '0');
				} else {
					httpParams = httpParams.set(key, value);
				}
			}
		}

		return this.httpClient.get(url, { params: httpParams, responseType: 'blob' });
	}
	
	// Methode d'obtention des suggestions de noms d'entreprises
	public getSuggestionsNomsEntreprises(criteria: string, quantity: number): Observable<Suggestions<ActivePossibilities, SuggestionNomEntreprise>> {
		let url = this.path + 'entreprises/suggestions-noms?criteria=' + encodeURI(criteria) + '&quantity=' + quantity;
		
		// On ajoute un header special pour empecher l'intercepteur d'afficher un indicateur de chargement
		let headers = new HttpHeaders().set("no-pending-trace", "true");
		
		let result = this.httpClient.get<Suggestions<ActivePossibilities, SuggestionNomEntreprise>>(url, { headers: headers });
		return result;
	}

}